/**
 * « Ta voix » (lot 6, moteur JRAF) : enregistrer la narration d'une scène,
 * la transcrire (edge fn `animation-voice` : Whisper, mots horodatés), la
 * déposer dans le bucket PUBLIC `animation-assets` (0671), puis la poser
 * dans le brouillon (`withVoice` de @jeraisonne/animation).
 *
 * Format : lu partout, iPhone compris. Safari et Chrome récents enregistrent
 * en m4a (AAC) : gardé tel quel. Sinon (webm / opus : Firefox, Chrome sous
 * Linux), la prise est convertie en wav mono 22 kHz dans le navigateur.
 */

import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import type { SupabaseClient } from '@supabase/supabase-js';
import { ASSET_PATH_RE } from '@jeraisonne/animation';
import { supabase } from '@/lib/supabase';

const db = supabase as unknown as SupabaseClient;

export const ANIMATION_ASSETS_BUCKET = 'animation-assets';

/** URL publique d'une ressource d'animation (voix enregistrée, image). */
export function animationAssetUrl(path: string): string {
  return db.storage.from(ANIMATION_ASSETS_BUCKET).getPublicUrl(path).data.publicUrl;
}

/** 2 Mo par scène (≈ 40 s en wav, bien plus en m4a) : même limite que le serveur. */
export const VOICE_MAX_BYTES = 2 * 1024 * 1024;

/**
 * Formats d'enregistrement, par préférence : m4a en AAC d'abord (Safari,
 * Chrome avec AAC : lu sur iPhone, gardé tel quel) ; sinon webm (converti en
 * wav, lu partout). « audio/mp4 » sans codec en dernier : un Chrome sans AAC
 * y mettrait de l'Opus, que tous les iPhone ne lisent pas.
 */
export const VOICE_RECORDER_MIME_TYPES = [
  'audio/mp4;codecs=mp4a.40.2',
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
];

export type VoiceExtension = 'm4a' | 'mp3' | 'aac' | 'wav';

/** Extension d'un audio lu partout ; `null` : à convertir (webm, ogg…). */
export function playableEverywhere(type: string, name = ''): VoiceExtension | null {
  const t = type.toLowerCase().split(';')[0]!.trim();
  if (t === 'audio/mp4' || t === 'audio/x-m4a' || t === 'audio/m4a') return 'm4a';
  if (t === 'audio/mpeg' || t === 'audio/mp3') return 'mp3';
  if (t === 'audio/aac' || t === 'audio/x-aac') return 'aac';
  if (t === 'audio/wav' || t === 'audio/x-wav' || t === 'audio/wave') return 'wav';
  if (t === '' || t === 'application/octet-stream') {
    const ext = name.toLowerCase().split('.').pop() ?? '';
    if (ext === 'm4a') return 'm4a';
    if (ext === 'mp3' || ext === 'aac' || ext === 'wav') return ext;
  }
  return null;
}

const MIME_OF: Record<VoiceExtension, string> = {
  m4a: 'audio/mp4',
  mp3: 'audio/mpeg',
  aac: 'audio/aac',
  wav: 'audio/wav',
};

/** WAV PCM 16 bits mono. */
export function encodeWav(samples: Float32Array, sampleRate: number): Blob {
  const buf = new ArrayBuffer(44 + samples.length * 2);
  const v = new DataView(buf);
  const ascii = (at: number, s: string) => {
    for (let i = 0; i < s.length; i++) v.setUint8(at + i, s.charCodeAt(i));
  };
  ascii(0, 'RIFF');
  v.setUint32(4, 36 + samples.length * 2, true);
  ascii(8, 'WAVE');
  ascii(12, 'fmt ');
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true); // PCM
  v.setUint16(22, 1, true); // mono
  v.setUint32(24, sampleRate, true);
  v.setUint32(28, sampleRate * 2, true);
  v.setUint16(32, 2, true);
  v.setUint16(34, 16, true);
  ascii(36, 'data');
  v.setUint32(40, samples.length * 2, true);
  for (let i = 0; i < samples.length; i++) {
    const x = Math.max(-1, Math.min(1, samples[i]!));
    v.setInt16(44 + i * 2, x < 0 ? x * 0x8000 : x * 0x7fff, true);
  }
  return new Blob([buf], { type: 'audio/wav' });
}

/** Décode une prise (webm, ogg…) et la réécrit en wav mono, 22 kHz. */
async function toWav(blob: Blob, rate = 22050): Promise<Blob> {
  const Ctx =
    typeof AudioContext !== 'undefined'
      ? AudioContext
      : (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctx || typeof OfflineAudioContext === 'undefined')
    throw new Error(
      'Ce navigateur ne sait pas convertir la prise : enregistre depuis Safari ou Chrome.',
    );
  const ctx = new Ctx();
  try {
    const decoded = await ctx.decodeAudioData(await blob.arrayBuffer());
    const frames = Math.max(1, Math.ceil(decoded.duration * rate));
    const off = new OfflineAudioContext(1, frames, rate);
    const src = off.createBufferSource();
    src.buffer = decoded; // mono : les canaux sont mélangés par le contexte
    src.connect(off.destination);
    src.start();
    const rendered = await off.startRendering();
    return encodeWav(rendered.getChannelData(0), rate);
  } finally {
    void ctx.close().catch(() => undefined);
  }
}

/** Prise prête à déposer : format lu partout, 2 Mo au plus. */
export async function prepareVoiceFile(blob: Blob, name = ''): Promise<File> {
  let ext = playableEverywhere(blob.type, name);
  let out: Blob = blob;
  if (!ext) {
    out = await toWav(blob);
    ext = 'wav';
  }
  if (out.size > VOICE_MAX_BYTES) throw new VoiceError('too_large');
  return new File([out], `voix.${ext}`, { type: MIME_OF[ext] });
}

// ----------------------------------------------------------- serveur

export const voiceUploadSchema = z.object({
  ok: z.literal(true),
  path: z.string().regex(ASSET_PATH_RE),
  durationMs: z.number().int().min(100),
  words: z
    .array(
      z.object({
        w: z.string().min(1).max(60),
        startMs: z.number().int().min(0),
        endMs: z.number().int().min(0),
      }),
    )
    .max(400),
  transcript: z.string(),
});
export type VoiceUpload = z.infer<typeof voiceUploadSchema>;

export const VOICE_ERROR_MESSAGES: Record<string, string> = {
  no_speech: 'Je n’ai entendu aucune parole : vérifie le micro, puis recommence.',
  too_large: 'Prise trop longue (2 Mo au plus) : coupe la scène, ou importe un m4a.',
  unsupported_format: 'Format refusé : m4a, mp3, aac ou wav (lus sur iPhone).',
  whisper_error: 'La transcription a échoué : réessaie dans un instant.',
  upload_failed: 'Le dépôt de la prise a échoué : réessaie.',
  not_found: 'Animation introuvable.',
  forbidden: 'Réservé aux admins.',
  invalid_input: 'Prise illisible : recommence.',
};

/** Pourquoi la transcription n'a pas reconnu de parole (`no_speech`). */
export const NO_SPEECH_MESSAGES: Record<string, string> = {
  empty:
    'La transcription n’a reconnu aucun mot. Réécoute la prise : si ta voix y est, garde-la quand même ; sinon vérifie le micro choisi.',
  no_speech:
    'La transcription n’y entend que du silence ou du bruit. Réécoute la prise : si ta voix y est, garde-la quand même.',
  hallucination:
    'La transcription n’a reconnu que du bruit. Réécoute la prise : si ta voix y est, garde-la quand même.',
};

export class VoiceError extends Error {
  constructor(
    readonly code: string,
    /** `no_speech` : raison donnée par le serveur (`empty`, `no_speech`, `hallucination`) */
    readonly reason: string | null = null,
    /** ce que la transcription a entendu */
    readonly heard: string | null = null,
  ) {
    super(
      (code === 'no_speech' && reason ? NO_SPEECH_MESSAGES[reason] : undefined) ??
        VOICE_ERROR_MESSAGES[code] ??
        'La prise n’a pas pu être enregistrée.',
    );
  }
}

/** Transcrit et dépose la prise ; renvoie chemin, durée et mots horodatés. */
export async function uploadVoice(input: {
  animationId: string;
  file: File;
  expected: string;
  /** garder la prise même si la transcription n'y entend pas de parole */
  force?: boolean;
}): Promise<VoiceUpload> {
  const form = new FormData();
  form.append('file', input.file);
  form.append('animation_id', input.animationId);
  form.append('expected', input.expected.slice(0, 1200));
  if (input.force) form.append('force', '1');
  const { data, error } = await supabase.functions.invoke('animation-voice', { body: form });
  if (error) {
    let code = 'unknown';
    let reason: string | null = null;
    let heard: string | null = null;
    const ctx = (error as Error & { context?: Response }).context;
    if (ctx && typeof ctx.json === 'function') {
      try {
        const parsed = (await ctx.json()) as {
          error?: string;
          reason?: unknown;
          transcript?: unknown;
        };
        if (parsed.error) code = parsed.error;
        if (typeof parsed.reason === 'string') reason = parsed.reason;
        if (typeof parsed.transcript === 'string' && parsed.transcript.trim())
          heard = parsed.transcript.trim();
      } catch {
        /* message générique */
      }
    }
    throw new VoiceError(code, reason, heard);
  }
  const parsed = voiceUploadSchema.safeParse(data);
  if (!parsed.success) throw new VoiceError('invalid_response');
  return parsed.data;
}

export function useUploadVoice() {
  return useMutation({ mutationFn: uploadVoice });
}
